import type {Auth, Comment, CommentCreationRequest, Document} from 'typings/portaro.be.types';
import type CommentsDataService from './comments.data-service';
import type LoginService from 'shared/login/login.service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';

export class KpCommentsPresenter {
    public static presenterName = 'kpCommentsPresenter';

    /*@ngInject*/
    protected constructor(private commentsDataService: CommentsDataService,
                          private loginService: LoginService,
                          private currentAuth: Auth,
                          private finishedResponseInteractionService: FinishedResponseInteractionService) {
    }

    public async loadComments(document: Document): Promise<Comment[]> {
        try {
            return await this.commentsDataService.loadComments(document.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return [];
        }
    }

    public async addAnonymousComment(document: Document): Promise<Comment | null> {
        try {
            return await this.addComment({record: document.id}, this.authEvided);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }

        return null;
    }

    public async addNonAnonymousComment(document: Document): Promise<Comment | null> {
        try {
            await this.loginService.login();
            return await this.addComment({record: document.id, creator: this.currentAuth.activeUser.id}, true);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        } finally {
            this.loginService.reloadIfChangedButNotReloaded();
        }

        return null;
    }

    public async deleteComment(comment: Comment): Promise<boolean> {
        try {
            const response = await this.commentsDataService.deleteComment(comment);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(response);
            return true;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return false;
        }
    }

    private async addComment(commentCreation: CommentCreationRequest, deletable: boolean): Promise<Comment> {
        const response = await this.commentsDataService.addComment(commentCreation);
        await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(response);
        response.savedObject.deletable = deletable;
        return response.savedObject;
    }

    public get authEvided(): boolean {
        return this.currentAuth.evided;
    }
}